<template>
    <div class="map-container">
        <plotly ref="map" :data="plotData" :layout="plotLayout" @relayout="onZoom" @autosize="onAutosize"
            :display-mode-bar="false"></plotly>
        <div ref="test"></div>
    </div>
</template>

<script>
import { Plotly } from 'vue-plotly';
import { debounce } from "lodash";

function computeControlLength(angle, r) {
    let alpha = 4 / 3 * Math.tan(angle / 4);
    return alpha * r;
}

function getControlPoints(p1, p2, angle, r) {
    let cL = computeControlLength(angle, r);
    let cAngle = angle / 2;
    let [x1, y1] = p1;
    let [x2, y2] = p2;
    let cp1 = [x1 + cL * Math.cos(cAngle), y1 - cL * Math.sin(cAngle)];
    let cp2 = [x2 - cL * Math.cos(cAngle), y2 - cL * Math.sin(cAngle)];
    return [cp1, cp2]
}

function rotatePoint(p, c, angle) {
    let [px, py] = p;
    let [cx, cy] = c;

    let rx = Math.cos(angle) * (px - cx) - Math.sin(angle) * (py - cy) + cx;
    let ry = Math.sin(angle) * (px - cx) + Math.cos(angle) * (py - cy) + cy;
    return [rx, ry];
}

function _getArcPath(c, angle, r) {
    let [xc, yc] = c;
    let y1 = yc - r * Math.cos(angle / 2);
    let x1 = xc - r * Math.sin(angle / 2);

    let y2 = y1;
    let x2 = x1 + 2 * r * Math.sin(angle / 2);
    let p1 = [x1, y1];
    let p2 = [x2, y2];

    let [cp1, cp2] = getControlPoints(p1, p2, angle, r);
    [x1, y1] = rotatePoint(p1, c, angle / 2);
    [x2, y2] = rotatePoint(p2, c, angle / 2);
    let [cx1, cy1] = rotatePoint(cp1, c, angle / 2);
    let [cx2, cy2] = rotatePoint(cp2, c, angle / 2);

    let path = `M ${xc},${yc} L ${x1},${y1} C ${cx1},${cy1} ${cx2},${cy2} ${x2},${y2} L ${xc},${yc} Z`;
    return path;
}

function calculateArcSegment(c, r, startAngle, endAngle) {
    const [xc, yc] = c;
    const segmentAngle = endAngle - startAngle;

    // Магическое число для приближения сегмента окружности кубической кривой Безье.
    const handleLengthFactor = (4 / 3) * Math.tan(segmentAngle / 4);
    const handleLength = r * handleLengthFactor;

    const startPoint = {
        x: xc + r * Math.cos(startAngle),
        y: yc + r * Math.sin(startAngle)
    };
    const endPoint = {
        x: xc + r * Math.cos(endAngle),
        y: yc + r * Math.sin(endAngle)
    };

    // Контрольная точка 1 - касательная к началу дуги
    const cp1 = {
        x: startPoint.x - handleLength * Math.sin(startAngle),
        y: startPoint.y + handleLength * Math.cos(startAngle)
    };

    // Контрольная точка 2 - касательная к концу дуги
    const cp2 = {
        x: endPoint.x + handleLength * Math.sin(endAngle),
        y: endPoint.y - handleLength * Math.cos(endAngle)
    };

    return { startPoint, endPoint, cp1, cp2 };
}

function getArcPath(c, angle, r) {
    const [xc, yc] = c;

    if (angle <= 0) return "";

    // Обрезаем угол до 2π, чтобы избежать проблем с плавающей точкой
    if (angle >= 2 * Math.PI) {
        angle = 2 * Math.PI - 0.0001;
    }

    const pathParts = [];
    pathParts.push(`M ${xc},${yc}`);

    const baseAngle = -Math.PI / 2;
    let currentAngle = baseAngle;
    const targetAngle = baseAngle + angle;
    const maxSegmentAngle = Math.PI / 2;

    let isFirstSegment = true;

    // Арка разбивается на сегменты по 90 градусов, чтобы избежать проблем с вываливанием за пределы окружности
    // на больших углах
    while (currentAngle < targetAngle) {
        const remainingAngle = targetAngle - currentAngle;
        const segmentAngle = Math.min(remainingAngle, maxSegmentAngle);
        const endOfSegmentAngle = currentAngle + segmentAngle;

        const segment = calculateArcSegment(c, r, currentAngle, endOfSegmentAngle);

        if (isFirstSegment) {
            // Для первого сегмента добавляем линию от центра до начальной точки дуги
            pathParts.push(`L ${segment.startPoint.x},${segment.startPoint.y}`);
            isFirstSegment = false;
        }

        // Кубическая кривая Безье для сегмента
        pathParts.push(`C ${segment.cp1.x},${segment.cp1.y} ${segment.cp2.x},${segment.cp2.y} ${segment.endPoint.x},${segment.endPoint.y}`);
        currentAngle = endOfSegmentAngle;
    }

    // Завершаем путь, соединяя его с центром
    pathParts.push('Z');

    return pathParts.join(' ');
}

function getData(gridValues, reservoir, panel, zCoordProp = 'y_coord') {
    if (!gridValues || gridValues?.length === 0) {
        return [];
    }

    let dyProp = 'd' + zCoordProp[0];
    let dx = reservoir.dx;
    let dy = reservoir[dyProp];

    let cellsY = gridValues.length || 0;
    let cellsX = gridValues && gridValues[0] ? gridValues[0].length : 0;
    let gridY = Array(cellsY).fill(0).map((v, i) => i * dy);
    let gridX = Array(cellsX).fill(0).map((v, i) => i * dx);

    let traces = [
        {
            x: gridX,
            y: gridY,
            z: gridValues,
            type: 'contour',
            colorscale: 'Jet',
            xaxis: 'x',
            yaxis: 'y',
            hovertemplate: `X: %{x}, Y: %{y}<br>${panel?.param}: %{z}<extra></extra>`,
            contours: {
                showlabels: true,
                labelfont: {
                    size: 12,
                }
            }
        }
    ];

    if (panel?.param === 'Sw') {
        traces[0]['zmin'] = 0;
        traces[0]['zmax'] = 1;
    }

    return traces;
}

function getLayout(gridValues, reservoir, panel, connections, wells, result, params = {}, zCoordProp = 'y_coord', timeIndex = 0) {
    let layout = {
        'height': 1000,
        'width': 1000,
        'xaxis': {
            'zeroline': false,
            'visible': false,
            'showgrid': true,
            'linecolor': '#cccccc',
            'matches': 'y',
        },
        'yaxis': {
            'zeroline': false,
            'visible': false,
            'showgrid': true,
            'linecolor': '#cccccc',
        },
    }

    let shownWellsIndexes = wells.reduce((aggr, well, index) => {
        if (well.shown) {
            aggr.push(index);
        }

        return aggr;
    }, []);
    let shownWells = wells.filter(well => well.shown);

    if (params.showAnnotations) {
        let annotations = [];
        if (gridValues) {
            for (let i = 0; i < gridValues.length; i++) {
                for (let j = 0; j < gridValues[0].length; j++) {
                    let currentValue = gridValues[i][j];
                    let textValue = parseFloat(currentValue).toFixed(2);
                    let textColor = 'transparent';
                    if (currentValue > 0) {
                        textColor = 'white';
                    }

                    let result = {
                        xref: 'x1',
                        yref: 'y1',
                        x: j,
                        y: i,
                        text: textValue,
                        font: {
                            family: 'Arial',
                            size: 8,
                            color: textColor
                        },
                        showarrow: false,
                    };

                    annotations.push(result);
                }
            }
        }

        layout['annotations'] = annotations;
    }

    let shapes = [];
    if (params.showConnections) {
        let minT = null;
        let maxT = null;
        for (let connection of connections) {
            let T = connection[2]?.T;
            if (!T) {
                continue;
            }

            if (T && minT === null) {
                minT = T;
                maxT = T;
            }

            if (T < minT) {
                minT = T;
            }

            if (T > maxT) {
                maxT = T;
            }
        }

        let connectionLines = connections.reduce((all, connection) => {
            let minWidth = 1;
            let maxWidth = 7;
            let T = connection[2]?.T;
            if (!T) {
                return all;
            }

            let width = minWidth + (T - minT) / (maxT - minT) * (maxWidth - minWidth);

            return all.concat([
                {
                    type: 'line',
                    x0: connection[0].x,
                    y0: connection[0].y,
                    x1: connection[1].x,
                    y1: connection[1].y,
                    opacity: 0.5,
                    line: {
                        color: 'white',
                        width: width + 1,
                    }
                },
                {
                    type: 'line',
                    x0: connection[0].x,
                    y0: connection[0].y,
                    x1: connection[1].x,
                    y1: connection[1].y,
                    opacity: 0.5,
                    line: {
                        color: 'black',
                        width,
                        //dash: 'dot'
                    }
                }
            ]);
        }, []);

        shapes = shapes.concat(connectionLines);
    }

    if (params.showWells) {
        let dx = reservoir.dx;
        let dy = reservoir.dz;

        let minWellRadius = Math.max(dx, dy) / 2;
        let maxWellRadius = Math.max(dx, dy) * 2;
        let defaultRadius = minWellRadius;
        let debits = [];
        let sw = [];

        if (result && result.wells && result.nodes) {
            debits = result.wells
                .filter((_, index) => shownWellsIndexes.includes(index))
                .map(wellResult => Math.abs(wellResult.debit[timeIndex]));
            sw = result.wells
                .map((_, wellIndex) => {
                    let nodeResult = result.nodes.find(node => node.well === wellIndex);
                    if (!nodeResult) {
                        return null;
                    }

                    return nodeResult;
                })
                .filter((_, index) => shownWellsIndexes.includes(index))
                .map(wellResult => wellResult.Sw ? wellResult.Sw[timeIndex] || 0 : 0);
        }

        let maxDebit = null;
        let minDebit = null;
        for (let debit of debits) {
            if (maxDebit === null || debit > maxDebit) {
                maxDebit = debit;
            }

            if (minDebit === null || debit < minDebit) {
                minDebit = debit;
            }
        }

        let wellShapes = shownWells.reduce((wellShapes, well, wellIndex) => {
            let fillColor = 'white';
            let lineColor = 'black';
            let lineWidth = 1;

            let drawAsPie = false;
            let radius = defaultRadius;
            let angle = 0;
            let wellDebit = debits[wellIndex] || null;
            let wellSw = sw[wellIndex] || null;

            if (wellDebit && wellSw) {
                drawAsPie = true;
                try {
                    radius = minWellRadius + (wellDebit - minWellRadius) / (maxDebit - minDebit) * (maxWellRadius - minWellRadius)
                }
                catch (e) {
                    radius = null;
                }

                if (!radius) {
                    radius = defaultRadius;
                }

                angle = 2 * Math.PI * (1 - wellSw);
            }

            let opacity = 0.8;
            if (well.closed) {
                fillColor = 'black';
                lineColor = 'white';
            }

            if (well.imaginary) {
                opacity = 0.5;
            }

            let graphWells = panel && panel?.graphWells || [];
            if (graphWells && !(graphWells instanceof Array)) {
                graphWells = [graphWells];
            }
            if (graphWells.includes(well.id)) {
                fillColor = 'red';
                lineColor = 'white';
                lineWidth = 2;
            }

            if (drawAsPie) {
                let x = well.x_coord;
                let y = well[zCoordProp];

                let mainCircle = {
                    type: 'circle',
                    xref: 'x',
                    yref: 'y',
                    x0: x - radius,
                    y0: y - radius,
                    x1: x + radius,
                    y1: y + radius,
                    opacity,
                    fillcolor: fillColor,
                    line: { color: lineColor, width: lineWidth }
                };

                let c = [x, y];

                //arc'и в plotly.js запрещены, поэтому приходится использовать
                //кривые безье
                //https://plotly.com/javascript/reference/#layout-shapes-items-shape-path
                //"Arcs (A) are not allowed because radius rx and ry are relative."
                let oilFraction = {
                    type: 'path',
                    path: getArcPath(c, angle, radius),
                    opacity,
                    fillcolor: 'black',
                    line: { color: 'black', width: lineWidth }
                };

                wellShapes.push(mainCircle);
                wellShapes.push(oilFraction);
            }
            else {
                let wellShape = {
                    type: 'circle',
                    xref: 'x',
                    yref: 'y',
                    x0: well.x_coord - radius,
                    y0: well[zCoordProp] - radius,
                    x1: well.x_coord + radius,
                    y1: well[zCoordProp] + radius,
                    opacity,
                    fillcolor: fillColor,
                    line: { color: lineColor, width: lineWidth }
                };
                wellShapes.push(wellShape);
            }

            return wellShapes;
        }, []);

        shapes = shapes.concat(wellShapes);
    }

    if (params.showWellLabels) {
        let annotations = layout['annotations'] || [];
        let fontSize = 12 * params.zoom;
        if (fontSize > params.maxFontSize) {
            fontSize = params.maxFontSize;
        }

        let labelsOfNormalSize = fontSize >= params.minFontSize;
        if (labelsOfNormalSize) {
            let wellLabels = shownWells.reduce((labels, well) => {
                return labels.concat([
                    {
                        x: well.x_coord,
                        y: well[zCoordProp] - 12,
                        xref: 'x',
                        yref: 'y',
                        text: well.id,
                        showarrow: false,
                        font: {
                            size: fontSize,
                            color: 'black',
                        }
                    }
                ]);
            }, []);
            annotations = annotations.concat(wellLabels);
            layout['annotations'] = annotations;
        }
    }

    if (shapes.length > 0) {
        layout['shapes'] = shapes;
    }

    return layout;
}

export default {
    props: ['reservoir', 'wells', 'connections', 'gridValues', 'zCoordProp', 'grids_y', 'dy', 'panel', 'result', 'type', 'timeIndex'],
    components: { Plotly },
    data() {
        let params = {
            showAnnotations: false,
            showWells: true,
            showWellLabels: true,
            showWellData: true,
            showConnections: true,
            minFontSize: 6,
            maxFontSize: 24,
            zoom: 1,
        }

        return {
            plotData: getData(this.gridValues, this.reservoir, this.panel, this.zCoordProp),
            plotLayout: getLayout(
                this.gridValues,
                this.reservoir,
                this.panel,
                this.connections,
                this.wells,
                this.result,
                params,
                this.zCoordProp,
                this.timeIndex
            ),
            params: { ...params },
            xrange: null,
            yrange: null,
            lastResizeTimestamp: null,
            repeatingEventSecondsLimit: 3,
            autoZoomFinished: false,
            debouncedAutozoomFinished: null,
            plotMounted: false,
        }
    },
    created() {
        this.debouncedAutozoomFinished = debounce(this.afterAutoZoomFinished, this.repeatingEventSecondsLimit * 1000);
    },
    watch: {
        gridValues() {
            this.updatePlot();
        },
        zoom() {
            this.updateFontSize();
        },
        plotMounted() {
            this.updateLayout();
        },
        wells: {
            deep: true,
            handler() {
                this.updatePlot();
            }
        },
        timeIndex: {
            deep: true,
            handler() {
                this.updatePlot();
            }
        }
    },
    methods: {
        afterAutoZoomFinished() {
            //this.onAutosize();
            this.autoZoomFinished = true;
            this.plotMounted = true;
        },
        onAutosize() {
            if (this.autoZoomFinished) {
                return;
            }

            let layoutData = this.$refs?.map?.layout;
            if (!layoutData) {
                return;
            }

            let eventTimestamp = (new Date).getTime() / 1000;
            let timeDelta = this.lastResizeTimestamp === null
                ? 0
                : eventTimestamp - this.lastResizeTimestamp;

            let isAutoZooming = timeDelta < this.repeatingEventSecondsLimit;
            this.lastResizeTimestamp = eventTimestamp;

            if (isAutoZooming) {
                this.xrange = layoutData?.xaxis?.range?.slice() || null;
                this.yrange = layoutData?.yaxis?.range?.slice() || null;
                this.params.zoom = 1;
                this.debouncedAutozoomFinished();
            }
            else {
                this.autoZoomFinished = true;
                this.plotMounted = true;
            }
        },
        onZoom(eventdata) {
            if (!this.xrange || !this.yrange) {
                return;
            }

            let isZoomEvent = typeof (eventdata['xaxis.range[0]']) !== 'undefined';
            if (isZoomEvent) {
                let newXrange = [eventdata['xaxis.range[0]'], eventdata['xaxis.range[1]']];
                let newYrange = [eventdata['yaxis.range[0]'], eventdata['yaxis.range[1]']];

                let oldXSize = this.xrange[1] - this.xrange[0];
                let oldYSize = this.yrange[1] - this.yrange[0];
                let oldDiag = Math.sqrt(oldXSize ** 2 + oldYSize ** 2);

                let newXSize = newXrange[1] - newXrange[0];
                let newYSize = newYrange[1] - newYrange[0];
                let newDiag = Math.sqrt(newXSize ** 2 + newYSize ** 2);

                this.zoom = oldDiag / newDiag;
            }
        },
        updatePlot() {
            this.updateData();
            this.updateLayout();
        },
        updateData() {
            this.plotData = getData(this.gridValues, this.reservoir, this.panel, this.zCoordProp);
        },
        updateLayout() {
            this.plotLayout = getLayout(
                this.gridValues,
                this.reservoir,
                this.panel,
                this.connections,
                this.wells,
                this.result,
                this.params,
                this.zCoordProp,
                this.timeIndex
            );
            this.$refs?.map?.relayout(this.plotLayout);
        },
        updateFontSize() {
            let plotLayout = getLayout();
            let annotations = plotLayout['annotations'] || [];
            this.$refs?.map?.relayout({ annotations });
        }
    },
    computed: {
    }
}

</script>

<style>
.well-id-text {
    font-size: 18px;
    paint-order: stroke;
    stroke: #000000;
    stroke-width: 1px;
    stroke-linecap: butt;
    stroke-linejoin: miter;
}
</style>